/**
 * Shared plain-English status formatter.
 *
 * The Precision Care UI is used by non-technical clinicians: it must NEVER
 * show a raw backend status enum (`pending_review`, `partially_adjudicated`,
 * …). Every surface that renders a status badge — the ingestion run ledger
 * (Explore → Log), a proposed change's adjudication status, and a document
 * version's lifecycle status — routes through this single formatter instead
 * of patching each site with its own ad-hoc label map.
 */

/**
 * Known backend status enums mapped to their plain-English label.
 *
 * Covers `IngestionRunStatus`, `IngestionChangeStatus`, `VersionStatus`, and
 * `DocumentIngestionStatus` (`app.models.ingestion` / `app.models.documents`).
 * Values shared by more than one enum (`confirmed`, `rejected`) intentionally
 * map to one label — they mean the same thing to a clinician regardless of
 * which record type carries them.
 */
const STATUS_LABELS: Record<string, string> = {
  // IngestionRunStatus
  pending_review: "Awaiting your review",
  partially_adjudicated: "Partially reviewed",
  superseded: "Superseded",
  // IngestionChangeStatus
  pending: "Awaiting your review",
  edited: "Edited & approved",
  confirmed: "Approved",
  rejected: "Rejected",
  // VersionStatus
  proposed: "Proposed",
  committed: "Committed",
  // DocumentIngestionStatus (ADR-047)
  not_ingested: "Not yet added to profile",
};

/**
 * Turn a snake_case status token into a human "Sentence case" label.
 *
 * @param value - The raw token.
 * @returns The humanised label (or the value unchanged if it has no body).
 */
function humanizeToken(value: string): string {
  const words = value.replace(/[-_]+/g, " ").trim();
  if (words === "") return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Human-readable label for a backend status enum, never the raw value.
 *
 * Unknown/future values fall back to a title-cased, de-underscored form
 * rather than surfacing the raw token.
 *
 * @param status - The backend status string.
 * @returns A plain-English label.
 */
export function humanizeStatus(status: string): string {
  return STATUS_LABELS[status] ?? humanizeToken(status);
}
